import { useRef, useState } from 'react';
import { T, Var } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Textarea } from 'src/components/ui/textarea';
import type { ImportFieldNode } from '@erp/rpc';
import {
  isExtendAISchemaShaped,
  parseExtendAISchema,
} from './extendAISchemaParser';
import { parseSamplePayload } from './samplePayloadParser';
import { parseSqlSchema } from './sqlSchemaParser';

interface JsonFieldImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user confirms the import. The dialog stays open while
   * isImporting is true. `keyedBy` is the composite key parsed from a "sql"
   * schema's `keyIndex` fields (field names, in key-column order) — always
   * `[]` for the Extend AI format, which has no such concept.
   */
  onImport: (fields: ImportFieldNode[], keyedBy: string[]) => void;
  isImporting?: boolean;
  importError?: string | null;
  /** 'sql' schemas parse with the restricted string/integer/numeric/date vocabulary (sqlSchemaParser) instead of the Extend AI one. */
  schemaType?: string;
}

/** Which parser produced the preview — shown so key-name handling is explicit. */
type ParsedAs = 'sql' | 'json-schema' | 'sample-payload';

type DialogStep =
  | { kind: 'paste'; rawJson: string; error: string | null }
  | {
      kind: 'preview';
      rawJson: string;
      fields: ImportFieldNode[];
      keyedBy: string[];
      parsedAs: ParsedAs;
    };

const PASTE_PLACEHOLDER = `{
  "schema": {
    "type": "object",
    "properties": {
      "purchase_orders": {
        "type": "array",
        "items": {
          "type": "object",
          "properties": {
            "purchase_order_number": { "type": ["string", "null"] }
          },
          "required": ["purchase_order_number"]
        }
      }
    }
  }
}`;

const SQL_PASTE_PLACEHOLDER = `{
  "schema": {
    "type": "object",
    "properties": {
      "item_no": { "type": "string", "required": true, "keyIndex": 0 },
      "qty_on_hand": { "type": "integer" },
      "unit_cost": {
        "type": "numeric",
        "typeArgs": { "precision": 13, "scale": 4 }
      },
      "received_on": { "type": "date", "default": "2026-01-01" }
    }
  }
}`;

export function JsonFieldImportDialog({
  open,
  onOpenChange,
  onImport,
  isImporting = false,
  importError = null,
  schemaType,
}: JsonFieldImportDialogProps) {
  const isSqlSchema = schemaType === 'sql';
  const [step, setStep] = useState<DialogStep>({
    kind: 'paste',
    rawJson: '',
    error: null,
  });

  function reset() {
    setStep({ kind: 'paste', rawJson: '', error: null });
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      reset();
      onOpenChange(false);
    } else {
      onOpenChange(true);
    }
  }

  function handleParse(rawJson: string) {
    if (isSqlSchema) {
      const result = parseSqlSchema(rawJson);
      if (!result.ok) {
        setStep({ kind: 'paste', rawJson, error: result.error });
        return;
      }
      setStep({
        kind: 'preview',
        rawJson,
        fields: result.fields,
        keyedBy: result.keyedBy,
        parsedAs: 'sql',
      });
      return;
    }

    // JSON-Schema-shaped input (`schema.properties` / bare `properties`) goes
    // through the Extend AI parser (keys camelCased); anything else is treated
    // as a raw sample payload (keys preserved verbatim).
    const parsedAs: ParsedAs = isExtendAISchemaShaped(rawJson)
      ? 'json-schema'
      : 'sample-payload';
    const result =
      parsedAs === 'json-schema'
        ? parseExtendAISchema(rawJson)
        : parseSamplePayload(rawJson);
    if (!result.ok) {
      setStep({ kind: 'paste', rawJson, error: result.error });
      return;
    }
    setStep({
      kind: 'preview',
      rawJson,
      fields: result.fields,
      keyedBy: [],
      parsedAs,
    });
  }

  function handleBackToPaste() {
    if (step.kind !== 'preview') return;
    setStep({ kind: 'paste', rawJson: step.rawJson, error: null });
  }

  function handleConfirm() {
    if (step.kind !== 'preview') return;
    onImport(step.fields, step.keyedBy);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <T>Import fields from JSON</T>
          </DialogTitle>
        </DialogHeader>

        {step.kind === 'paste' ? (
          <PasteStep
            rawJson={step.rawJson}
            error={step.error}
            isSqlSchema={isSqlSchema}
            onChangeRaw={(raw) =>
              setStep({ kind: 'paste', rawJson: raw, error: null })
            }
            onParse={() => handleParse(step.rawJson)}
          />
        ) : (
          <PreviewStep
            fields={step.fields}
            keyedBy={step.keyedBy}
            parsedAs={step.parsedAs}
            isImporting={isImporting}
            importError={importError}
            onBack={handleBackToPaste}
            onConfirm={handleConfirm}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PasteStep({
  rawJson,
  error,
  isSqlSchema,
  onChangeRaw,
  onParse,
}: {
  rawJson: string;
  error: string | null;
  isSqlSchema: boolean;
  onChangeRaw: (raw: string) => void;
  onParse: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setFileError(null);
    try {
      const text = await file.text();
      onChangeRaw(text);
    } catch {
      setFileError(
        'Could not read the selected file. Make sure it is a UTF-8 JSON file.',
      );
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {isSqlSchema ? (
          <T>
            Paste a flat schema (or load it from a file). Each field is{' '}
            <Var>
              <code>
                {'{ "type": "string" | "integer" | "numeric" | "date" }'}
              </code>
            </Var>
            , with <Var>{'"typeArgs": { "precision", "scale" }'}</Var> nested
            for <Var>numeric</Var>, plus optional <Var>required</Var>,{' '}
            <Var>default</Var>, and a sequential <Var>keyIndex</Var> (0, 1, 2…)
            on the fields that form the composite key. Field names get converted
            from snake_case to camelCase.
          </T>
        ) : (
          <T>
            Paste an Extend AI extraction config (the JSON Schema shape used in
            code, e.g.{' '}
            <Var>
              <code>PURCHASE_ORDERS</code>
            </Var>
            ) or a raw sample payload (an example request body), or load either
            from a file. JSON Schemas get field names converted from snake_case
            to camelCase; sample payloads keep key names exactly as pasted and
            infer types from the values. Top-level wrappers like{' '}
            <Var>
              <code>purchase_orders</code>
            </Var>{' '}
            are kept so multi-entity documents extract correctly.
          </T>
        )}
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          void handleFileChange(e);
        }}
      />
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <T context="button label to load JSON from a file">Load from file</T>
        </Button>
      </div>
      <Textarea
        value={rawJson}
        onChange={(e) => onChangeRaw(e.target.value)}
        placeholder={isSqlSchema ? SQL_PASTE_PLACEHOLDER : PASTE_PLACEHOLDER}
        rows={16}
        className="font-mono text-xs"
      />
      {fileError && <p className="text-sm text-destructive">{fileError}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button onClick={onParse} disabled={!rawJson.trim()}>
          <T context="button label to parse JSON schema">Parse</T>
        </Button>
      </div>
    </div>
  );
}

function PreviewStep({
  fields,
  keyedBy,
  parsedAs,
  isImporting,
  importError,
  onBack,
  onConfirm,
}: {
  fields: ImportFieldNode[];
  keyedBy: string[];
  parsedAs: ParsedAs;
  isImporting: boolean;
  importError: string | null;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const totalCount = countFieldsDeep(fields);
  const keyIndexByFieldName = new Map(
    keyedBy.map((name, index) => [name, index]),
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <T>
          Review the parsed fields. <Var>{totalCount}</Var> field
          <Var>{totalCount === 1 ? '' : 's'}</Var> will be added to the schema.
        </T>
      </p>
      {parsedAs === 'sample-payload' && (
        <p className="text-xs text-muted-foreground">
          <T>
            Parsed as a sample payload — key names are kept exactly as pasted
            and types are inferred from the sample values.
          </T>
        </p>
      )}
      {parsedAs === 'json-schema' && (
        <p className="text-xs text-muted-foreground">
          <T>
            Parsed as a JSON Schema — field names are converted from snake_case
            to camelCase.
          </T>
        </p>
      )}
      {keyedBy.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <T>
            Key (in order): <Var>{keyedBy.join(', ')}</Var>
          </T>
        </p>
      )}
      <div className="rounded border p-2 text-xs">
        {fields.map((field, index) => (
          <PreviewFieldNode
            key={`${field.fieldName}-${index}`}
            field={field}
            depth={0}
            keyIndexByFieldName={keyIndexByFieldName}
          />
        ))}
      </div>
      {importError && <p className="text-sm text-destructive">{importError}</p>}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} disabled={isImporting}>
          <T context="button label to go back to paste step">Back</T>
        </Button>
        <Button onClick={onConfirm} disabled={isImporting}>
          {isImporting ? (
            <T context="button label while import is in progress">
              Importing...
            </T>
          ) : (
            <T context="button label to confirm import of schema fields">
              Import <Var>{totalCount}</Var> fields
            </T>
          )}
        </Button>
      </div>
    </div>
  );
}

function PreviewFieldNode({
  field,
  depth,
  keyIndexByFieldName,
}: {
  field: ImportFieldNode;
  depth: number;
  keyIndexByFieldName: Map<string, number>;
}) {
  const hasChildren = (field.children?.length ?? 0) > 0;
  const keyIndex = keyIndexByFieldName.get(field.fieldName);
  return (
    <div>
      <div
        className="flex items-center gap-2 py-1"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        <span className="font-mono">{field.fieldName}</span>
        <Badge variant="outline" className="text-xs">
          {field.fieldType}
        </Badge>
        {field.isRequired && (
          <Badge variant="secondary" className="text-xs">
            <T context="badge indicating a field is required">required</T>
          </Badge>
        )}
        {keyIndex !== undefined && (
          <Badge variant="secondary" className="text-xs">
            <T>
              key #<Var>{keyIndex}</Var>
            </T>
          </Badge>
        )}
        {field.enumValues && field.enumValues.length > 0 && (
          <span className="text-muted-foreground">
            {field.enumValues.length === 1 ? (
              <T context="enum field value count, singular">
                (<Var>{field.enumValues.length}</Var> value)
              </T>
            ) : (
              <T context="enum field value count, plural">
                (<Var>{field.enumValues.length}</Var> values)
              </T>
            )}
          </span>
        )}
        {field.fieldType === 'numeric' &&
          field.validationRules?.precision != null &&
          field.validationRules?.scale != null && (
            <span className="font-mono text-muted-foreground">
              <T context="numeric field precision/scale badge, e.g. NUMERIC(13, 4)">
                NUMERIC(<Var>{field.validationRules.precision}</Var>,{' '}
                <Var>{field.validationRules.scale}</Var>)
              </T>
            </span>
          )}
      </div>
      {hasChildren &&
        field.children?.map((child, index) => (
          <PreviewFieldNode
            key={`${child.fieldName}-${index}`}
            field={child}
            depth={depth + 1}
            keyIndexByFieldName={keyIndexByFieldName}
          />
        ))}
    </div>
  );
}

export function countFieldsDeep(fields: ImportFieldNode[]): number {
  let total = 0;
  for (const field of fields) {
    total += 1;
    if (field.children?.length) {
      total += countFieldsDeep(field.children);
    }
  }
  return total;
}
